import React from 'react';
import PropTypes from 'prop-types';
import ListItem from 'components/v2/list/ListItem';
import { useTranslation } from 'react-i18next';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import {
  useUpdateQuickPay,
  useUpdateActiveQuickPay,
} from 'hooks/quick-pay/useContracts';
import { showToast } from 'stores/ToastStore';
import moment from 'moment-timezone';
import ContractSummary from './ContractSummary';
import ContractForm from './ContractForm';

const ContractListItem = ({
  actionsEnabled,
  onDelete,
  data = {},
  activeTermsAndConditions = [],
}) => {
  const { t } = useTranslation();
  const isActiveContract = data?.active && data?.status === 'active';

  const onSaveSuccess = (response) => {
    if (response?.validation_errors?.length) {
      const { validation_errors: errors } = response;
      let errorMessages = '';
      if (errors.length === 1) {
        errorMessages = errors[0]?.message;
      } else {
        errorMessages = errors.reduce(
          (acc, { message }, idx) => acc + `${idx + 1}. ${message} \n`,
          '',
        );
      }
      showToast(errorMessages, errorMessages, 'error');
    } else if (response.updatedContract || response.contract) {
      showToast(
        t('UPDATE_QUICKPAY_SUCCESS'),
        t('UPDATE_QUICKPAY_SUCCESS'),
        'success',
      );
    }
  };

  const onSaveFail = (error) => {
    const response = error?.entity;
    if (response?.validation_errors?.length && !response?.success) {
      const { validation_errors: errors } = response;
      let errorMessages = '';
      if (errors.length === 1) {
        errorMessages = errors[0]?.message;
      } else {
        errorMessages = errors.reduce(
          (acc, { message }, idx) => acc + `${idx + 1}. ${message} \n`,
          '',
        );
      }
      showToast('', errorMessages, 'error');
    } else {
      showToast(error, error.message, 'error');
    }
  };

  const { mutation } = useUpdateQuickPay(onSaveSuccess, onSaveFail);

  const { mutation: updateActiveQuickPay } = useUpdateActiveQuickPay(
    onSaveSuccess,
    onSaveFail,
  );

  const onValidSuccess = (onSaveFormSuccess, switchView) => (newData) => {
    const start_date = moment(newData.start_date).format('YYYY-MM-DD');
    const end_date = moment(newData.end_date).format('YYYY-MM-DD');
    if (!(data.start_date === newData.start_date)) {
      newData.start_date = start_date;
    }
    if (!(data.end_date === newData.end_date)) {
      newData.end_date = end_date;
    }
    const payload = Object.keys(newData).reduce((acc, key) => {
      if (newData[key] !== data[key]) {
        acc[key] = newData[key];
      }
      return acc;
    }, {});

    if (Object.keys(payload).length) {
      const canUseUpdateApi =
        payload.end_date &&
        isActiveContract &&
        payload.end_date === moment(new Date()).format('YYYY-MM-DD');

      if (isActiveContract && !canUseUpdateApi) {
        handleEditActiveQuickpay(payload, onSaveFormSuccess, switchView);
      } else {
        handleEditSave(payload, onSaveFormSuccess, switchView);
      }
    } else {
      showToast('', t('NOTHING_TO_UPDATE'), 'warning');
      onSaveFormSuccess();
    }
  };

  const onValidFail = (validFail) => () => {
    validFail();
  };

  const handleEditActiveQuickpay = async (
    updatedContract,
    afterSave,
    switchFormView,
  ) => {
    try {
      await updateActiveQuickPay.mutateAsync({
        contractId: data._id,
        updates: updatedContract,
      });
      afterSave();
      switchFormView();
    } catch (e) {
      afterSave();
    }
  };

  const handleEditSave = async (updatedContract, afterSave, switchFormView) => {
    try {
      await mutation.mutateAsync({
        contractId: data._id,
        updates: updatedContract,
      });
      afterSave();
      switchFormView();
    } catch (e) {
      afterSave();
    }
  };

  const renderForm = (
    saving,
    handleSaveSuccess,
    handleSaveFail,
    hideFormView,
  ) => {
    return (
      <ContractForm
        data={data}
        saving={saving}
        onValidSuccess={onValidSuccess(handleSaveSuccess, hideFormView)}
        onValidFail={onValidFail(handleSaveFail)}
        activeTermsAndConditions={activeTermsAndConditions}
      />
    );
  };

  const handleShowEditMode = (showFormView) => () => {
    showFormView();
  };

  const handleDelete = () => {
    onDelete(data);
  };

  const renderActions = (showFormView, selectView) => {
    return (
      <>
        <Dropdown.Button
          dataTestId="quickpay-list-item-edit"
          onClick={handleShowEditMode(showFormView)}
        >
          {t('EDIT')}
        </Dropdown.Button>
        {!isActiveContract && (
          <Dropdown.Button
            dataTestId="quickpay-list-item-delete"
            onClick={() => {
              handleDelete();
            }}
          >
            {t('DELETE')}
          </Dropdown.Button>
        )}
      </>
    );
  };
  return (
    <ListItem
      testDataId={`quickpay-list-item-${data._id}`}
      actionsEnabled={actionsEnabled}
      actions={renderActions}
      form={renderForm}
    >
      {() => <ContractSummary data={data} />}
    </ListItem>
  );
};

ContractListItem.propTypes = {
  actionsEnabled: PropTypes.bool,
  defaultOpen: PropTypes.bool,
};

ContractListItem.defaultProps = {
  actionsEnabled: true,
  defaultOpen: false,
};

export default ContractListItem;
